import {PayloadAction, createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {keyBy} from 'es-toolkit'

import config from '../../config'

import {AppState} from '../store'

import {HttpStatusError} from '../../../../commons/src/libs/http-json'
import {cancelablePromise} from '../../../../commons/src/libs/promise'
import {
  getDateXDaysBefore,
  getEndOfDay,
  getStartOfDay,
  getTodaysDate,
} from '../../../../commons/src/services/date'
import * as localStorage from '../../../../commons/src/services/local-storage'
import {transformInternalOrder} from '../../libs/order'
import {StringMatchingFilter} from '../../libs/table-row-filter'
import * as internalApi from '../../services/api/internal'
import * as partnerApi from '../../services/api/partner'
import {captureException, captureIgnoredException} from '../../services/logging'

import {DateRange} from '../../../../commons/src/types'

import {Order, OrderNumber, OrderStatusType} from '../../order-types'

import {signOut} from './auth'

export interface OrderState {
  orders: Record<OrderNumber, Order>
  isLoading: boolean
  failed: boolean
  statusFilter: StringMatchingFilter<{
    status: OrderStatusType
  }>
  creationDateRange: DateRange
}

export const getInitialStatusFilter = () =>
  new StringMatchingFilter({
    key: 'status',
    filterValues: (localStorage.getItem(config.localStorageKeys.defaultStatusFilters) ||
      config.defaultStatusFilters) as string[],
  })

export const initialCreationDateRange = {
  from: getDateXDaysBefore(getTodaysDate(), config.dateRangeConfig.creationDate.defaultFromDays),
  to: getDateXDaysBefore(getTodaysDate(), config.dateRangeConfig.creationDate.defaultToDays),
}

const initialState: OrderState = {
  orders: {},
  isLoading: false,
  failed: false,
  statusFilter: getInitialStatusFilter(),
  creationDateRange: initialCreationDateRange,
}

export const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    setCreationDateRange: (state, action: PayloadAction<{creationDateRange: DateRange}>) => {
      state.creationDateRange = action.payload.creationDateRange
    },
    setStatusFilter: (
      state,
      action: PayloadAction<{statusFilter: StringMatchingFilter<{status: OrderStatusType}>}>
    ) => {
      state.statusFilter = action.payload.statusFilter
    },
    setOrders: (state, action: PayloadAction<{orders: Order[]}>) => {
      state.orders = keyBy(action.payload.orders, order => order.orderId)
      state.isLoading = false
      state.failed = false
    },
    setOrder: (state, action: PayloadAction<{order: Order}>) => {
      state.orders = keyBy(
        [...Object.values(state.orders), action.payload.order],
        (order: Order) => order.orderId
      )
    },
    setOrdersLoading: state => {
      state.isLoading = true
      state.failed = false
    },
    setOrdersFailed: state => {
      state.orders = {}
      state.isLoading = false
      state.failed = true
    },
  },
  extraReducers: builder => {
    builder.addCase(signOut.pending, () => {
      return initialState
    })
  },
})

export const {
  setCreationDateRange,
  setStatusFilter,
  setOrder,
  setOrders,
  setOrdersLoading,
  setOrdersFailed,
} = orderSlice.actions

let currentLoadOrdersCancel: (() => void) | undefined

export const loadOrders = createAsyncThunk('order/loadOrders', async (_, {getState, dispatch}) => {
  dispatch(setOrdersLoading())

  if (currentLoadOrdersCancel) {
    currentLoadOrdersCancel()
  }

  const state = getState() as AppState

  const filterValues = state.order.statusFilter.serialize().filterValues as OrderStatusType[]
  const dateRange = {
    from: getStartOfDay(state.order.creationDateRange.from),
    to: getEndOfDay(state.order.creationDateRange.to),
  }
  const providerId = state.core.provider!.id

  if (state.auth.isAdminMode) {
    // Use internal API
    const {promise: promiseLoadOrders, cancel: cancelLoadOrders} = cancelablePromise(
      internalApi.getOrders(providerId, dateRange, filterValues)
    )
    currentLoadOrdersCancel = cancelLoadOrders

    promiseLoadOrders.then(orders =>
      dispatch(setOrders({orders: orders.map(order => transformInternalOrder(order, providerId))}))
    )
    return
  }

  // Use Partner API
  const {promise: promiseLoadOrders, cancel: cancelLoadOrders} = cancelablePromise(
    partnerApi.getOrders(dateRange, filterValues)
  )

  currentLoadOrdersCancel = cancelLoadOrders

  await promiseLoadOrders
    .then(orders => {
      dispatch(setOrders({orders}))
    })
    .catch(error => {
      if (error && error.name === 'PromiseCancelledError') {
        return
      }

      if (error && error instanceof HttpStatusError && error.response.status === 401) {
        captureIgnoredException(error)
        dispatch(signOut())
        return
      }

      // No error handling here except tracking. If orders could not be loaded we inform the partner about it on the orders page.
      captureException(error)
      dispatch(setOrdersFailed())
    })
})

export default orderSlice.reducer
